import React, { useEffect, useRef, useState } from 'react';
import { BUYER_5MIL_HELP } from './brokerViewTokens';
import { siftApiClient } from '../../api/siftApiClient';
import type {
  BrokerBuyerSearchResult,
  BrokerSellerGroupSearchResult,
  BrokerSellerSearchResult,
  BrokerVinSearchResult,
} from '../../api/types';
import './BrokerBoardSearch.css';

interface BrokerBoardSearchProps {
  /**
   * The board's seller filter tokens (seller UIDs). Multi-select, same contract as
   * the Client View search. A dealer group contributes its member UIDs rather than
   * a name token, because the vehicle Parquet carries no parent column yet.
   */
  selectedSellers: string[];
  onChangeSellers: (next: string[]) => void;
  /** Jump to a buyer on the Buyer tab (§14.17). Toggles — the buyer focus is a
   *  multi-select, same as the seller filter. */
  onPickBuyer: (buyerUid: string, buyerName?: string | null) => void;
  /** Open a VIN's detail pop-out (and highlight its seller in the carousel). */
  onPickVin: (vin: string, sellerUid?: string | null) => void;
  /** §6.7 filter the whole board to a year/make/model phrase rather than opening
   *  one VIN. Omitting it hides the action. */
  onFilterVehicles?: (query: string) => void;
  /** The phrase the board is currently filtered to, so the action can show state. */
  vehicleFilter?: string;
  /** The focused buyers, shown alongside the seller filter as one selection list. */
  focusedBuyers?: { uid: string; name: string | null }[];
  onToggleBuyer?: (uid: string, name?: string | null) => void;
  onClearBuyers?: () => void;
  /** The board's entry filters, so results and their counts match the live board. */
  marketCenters?: string[];
  auctions?: string[];
}

const MIN_LEN = 2;
const DEBOUNCE_MS = 300;

/**
 * §14.18 — an open entity search on the board: type a buyer, dealer group, seller,
 * or VIN and either filter the board (sellers / groups, multi-select) or jump
 * straight to it (buyers / VINs). One request per keystroke fans out server-side.
 */
export const BrokerBoardSearch: React.FC<BrokerBoardSearchProps> = ({
  selectedSellers,
  onChangeSellers,
  onPickBuyer,
  onPickVin,
  onFilterVehicles,
  vehicleFilter = '',
  focusedBuyers = [],
  onToggleBuyer,
  onClearBuyers,
  marketCenters = [],
  auctions = [],
}) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buyers, setBuyers] = useState<BrokerBuyerSearchResult[]>([]);
  const [sellers, setSellers] = useState<BrokerSellerSearchResult[]>([]);
  const [vins, setVins] = useState<BrokerVinSearchResult[]>([]);
  const [parents, setParents] = useState<BrokerSellerGroupSearchResult[]>([]);
  // uid -> seller name, so a token reads as a name rather than a number.
  const [labels, setLabels] = useState<Record<string, string>>({});
  // group name -> its member uids, so a picked group renders as ONE row instead of
  // up to 24 seller rows.
  const [groups, setGroups] = useState<Record<string, string[]>>({});
  const abortRef = useRef<AbortController | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < MIN_LEN) {
      setBuyers([]); setSellers([]); setVins([]); setParents([]); setLoading(false);
      abortRef.current?.abort();
      return;
    }
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      // §14.18: ONE request per keystroke (the omnibox endpoint fans out to the
      // board lookups server-side) — fewer round-trips, snappier typeahead.
      siftApiClient.searchBrokerBoard(trimmed, 8, controller.signal, {
        market_center: marketCenters.length > 0 ? marketCenters.join(',') : undefined,
        auction: auctions.length > 0 ? auctions.join(',') : undefined,
      })
        .then((res) => {
          if (controller.signal.aborted) return;
          setParents(res.parents || []);
          setBuyers(res.buyers || []);
          setSellers(res.sellers || []);
          setVins(res.vins || []);
          setLoading(false);
        })
        .catch((err) => {
          if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
          setBuyers([]); setSellers([]); setVins([]); setParents([]); setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
    // Re-runs when the entry filters change so the offered options stay in step
    // with the board (joined here rather than passed as arrays to keep the deps stable).
  }, [q, marketCenters.join(','), auctions.join(',')]);

  // Close the results on an outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const close = () => { setOpen(false); setQ(''); };

  const toggleSeller = (uid: string, name?: string | null) => {
    if (!uid) return;
    if (name) setLabels((m) => ({ ...m, [uid]: name }));
    onChangeSellers(
      selectedSellers.includes(uid)
        ? selectedSellers.filter((s) => s !== uid)
        : [...selectedSellers, uid],
    );
    setQ('');
  };

  /** A group contributes ALL its board seller ids, so the board filters on the
   *  whole group; removing it takes exactly those ids back out. */
  const toggleGroup = (g: BrokerSellerGroupSearchResult) => {
    const ids = g.seller_uids || [];
    if (ids.length === 0) return;
    setGroups((m) => ({ ...m, [g.client_parent_name]: ids }));
    const applied = ids.every((u) => selectedSellers.includes(u));
    onChangeSellers(
      applied
        ? selectedSellers.filter((u) => !ids.includes(u))
        : [...selectedSellers, ...ids.filter((u) => !selectedSellers.includes(u))],
    );
    setQ('');
  };

  // Picked groups render as one row; the rest show as individual sellers.
  const activeGroups = Object.entries(groups).filter(
    ([, ids]) => ids.length > 0 && ids.every((u) => selectedSellers.includes(u)),
  );
  const groupedUids = new Set(activeGroups.flatMap(([, ids]) => ids));
  const looseSellers = selectedSellers.filter((u) => !groupedUids.has(u));
  const vehicleToken = vehicleFilter.trim();
  const selectionCount =
    activeGroups.length + looseSellers.length + focusedBuyers.length + (vehicleToken ? 1 : 0);

  const clearAll = () => {
    onChangeSellers([]);
    setGroups({});
    onClearBuyers?.();
    onFilterVehicles?.('');
  };

  const hasResults = buyers.length > 0 || sellers.length > 0 || vins.length > 0 || parents.length > 0;
  const searching = q.trim().length >= MIN_LEN;
  // Open on focus even with an empty box so the current selection is reachable.
  const showDropdown = open && (searching || selectionCount > 0);
  const placeholder = selectionCount > 0
    ? `${selectionCount} filter${selectionCount === 1 ? '' : 's'} applied`
    : 'Search 5mil · buyers · groups · sellers · VINs';

  return (
    <div className="bbs-wrap" ref={wrapRef}>
      <div className={`bbs-field ${selectionCount > 0 ? 'has-selection' : ''}`}>
        <input
          type="text"
          className="bbs-input"
          placeholder={placeholder}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          aria-label="Search buyers by 5mil or name, dealer groups, sellers, or VINs"
        />
        {(q || selectionCount > 0) && (
          <button
            type="button"
            className="bbs-clear"
            onClick={() => { setQ(''); clearAll(); setOpen(false); }}
            aria-label="Clear search and board filters"
            title="Clear search and board filters"
          >
            ✕
          </button>
        )}
      </div>
      {showDropdown && (
        <div className="bbs-results" role="listbox">
          {selectionCount > 0 && (
            <div className="bbs-group">
              <div className="bbs-group-head">
                <span>Selected · {selectionCount}</span>
                <button type="button" className="bbs-clear-all" onClick={clearAll}>Clear all</button>
              </div>
              {vehicleToken && onFilterVehicles && (
                <button
                  key="vf"
                  type="button"
                  className="bbs-item"
                  onClick={() => onFilterVehicles('')}
                  title={`Remove the “${vehicleToken}” board filter`}
                >
                  <span className="bbs-check">✓</span>
                  <span className="bbs-item-body">
                    <span className="bbs-item-name">
                      <span className="bbs-badge bbs-badge-vin">Filter</span>
                      {vehicleToken}
                    </span>
                    <span className="bbs-item-meta">Board limited to these vehicles</span>
                  </span>
                </button>
              )}
              {focusedBuyers.map((b) => (
                <button
                  key={`fb:${b.uid}`}
                  type="button"
                  className="bbs-item"
                  onClick={() => onToggleBuyer?.(b.uid, b.name)}
                  title={`Remove ${b.name || b.uid}`}
                >
                  <span className="bbs-check">✓</span>
                  <span className="bbs-item-body">
                    <span className="bbs-item-name">
                      <span className="bbs-badge bbs-badge-buyer">Buyer</span>
                      {b.name || `Buyer ${b.uid}`}
                    </span>
                    <span className="bbs-item-meta">#{b.uid}</span>
                  </span>
                </button>
              ))}
              {activeGroups.map(([name, ids]) => (
                <button
                  key={`sg:${name}`}
                  type="button"
                  className="bbs-item"
                  onClick={() => toggleGroup({ client_parent_name: name, seller_uids: ids, sellers: ids.length, vins: 0 })}
                  title={`Remove ${name}`}
                >
                  <span className="bbs-check">✓</span>
                  <span className="bbs-item-body">
                    <span className="bbs-item-name">
                      <span className="bbs-badge">Group</span>
                      {name}
                    </span>
                    <span className="bbs-item-meta">{ids.length} seller{ids.length === 1 ? '' : 's'} on the board</span>
                  </span>
                </button>
              ))}
              {looseSellers.map((uid) => (
                <button
                  key={`ss:${uid}`}
                  type="button"
                  className="bbs-item"
                  onClick={() => toggleSeller(uid)}
                  title={`Remove ${labels[uid] || uid}`}
                >
                  <span className="bbs-check">✓</span>
                  <span className="bbs-item-body">
                    <span className="bbs-item-name">
                      <span className="bbs-badge bbs-badge-seller">Seller</span>
                      {labels[uid] || `Seller ${uid}`}
                    </span>
                    <span className="bbs-item-meta">#{uid}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {searching && loading && <div className="bbs-state">Searching…</div>}
          {searching && !loading && !hasResults && <div className="bbs-state">Nothing matches “{q.trim()}”.</div>}
          {!searching && selectionCount === 0 && (
            <div className="bbs-state">Type at least {MIN_LEN} characters to search.</div>
          )}

          {/* Groups filter the board to every one of their sellers, which is a much
              wider action than picking one dealer — so it leads and is badged. */}
          {searching && parents.length > 0 && (
            <div className="bbs-group">
              <div className="bbs-group-head"><span>Dealer groups (parent)</span></div>
              {parents.map((p) => {
                const applied = (p.seller_uids || []).length > 0
                  && p.seller_uids.every((u) => selectedSellers.includes(u));
                return (
                  <button
                    key={`p:${p.client_parent_name}`}
                    type="button"
                    className="bbs-item"
                    onClick={() => toggleGroup(p)}
                    title={`Filter the board to all ${p.sellers} board sellers under ${p.client_parent_name}`}
                  >
                    <span className="bbs-check">{applied ? '✓' : ''}</span>
                    <span className="bbs-item-body">
                      <span className="bbs-item-name">
                        <span className="bbs-badge">Group</span>
                        {p.client_parent_name}
                      </span>
                      <span className="bbs-item-meta">
                        {p.sellers.toLocaleString()} seller{p.sellers === 1 ? '' : 's'}
                        {` · ${p.vins.toLocaleString()} VINs on the board`}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {searching && buyers.length > 0 && (
            <div className="bbs-group">
              <div className="bbs-group-head"><span>Buyers (individual)</span></div>
              {buyers.map((b) => (
                <button
                  key={`b:${b.buyer_uid}`}
                  type="button"
                  className="bbs-item"
                  onClick={() => { onPickBuyer(b.buyer_uid, b.buyer_name); setQ(''); }}
                  title={`Shop the board for ${b.buyer_name || b.buyer_uid}`}
                >
                  <span className="bbs-check">
                    {focusedBuyers.some((f) => f.uid === b.buyer_uid) ? '✓' : ''}
                  </span>
                  <span className="bbs-item-body">
                    <span className="bbs-item-name">
                      <span className="bbs-badge bbs-badge-buyer">Buyer</span>
                      {b.buyer_name || `Buyer ${b.buyer_uid}`}
                    </span>
                    <span className="bbs-item-meta" title={BUYER_5MIL_HELP}>
                      #{b.buyer_uid}
                      {b.buyer_parent_name ? ` · ${b.buyer_parent_name}` : ''}
                      {b.total_buys != null ? ` · ${b.total_buys.toLocaleString()} buys` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {searching && sellers.length > 0 && (
            <div className="bbs-group">
              <div className="bbs-group-head"><span>Sellers (individual)</span></div>
              {sellers.map((s) => (
                <button
                  key={`s:${s.seller_uid}`}
                  type="button"
                  className="bbs-item"
                  onClick={() => toggleSeller(s.seller_uid, s.seller_name)}
                  title={`Filter the board to ${s.seller_name || s.seller_uid}`}
                >
                  <span className="bbs-check">{selectedSellers.includes(s.seller_uid) ? '✓' : ''}</span>
                  <span className="bbs-item-body">
                    <span className="bbs-item-name">
                      <span className="bbs-badge bbs-badge-seller">Seller</span>
                      {s.seller_name || `Seller ${s.seller_uid}`}
                    </span>
                    <span className="bbs-item-meta">#{s.seller_uid}{s.vins != null ? ` · ${s.vins.toLocaleString()} VINs` : ''}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {searching && vins.length > 0 && (
            <div className="bbs-group">
              <div className="bbs-group-head"><span>VINs (open)</span></div>
              {onFilterVehicles && (
                <button
                  type="button"
                  className="bbs-item"
                  onClick={() => { onFilterVehicles(q.trim()); close(); }}
                  title="Narrow the whole board to these vehicles instead of opening one"
                >
                  <span className="bbs-check">{vehicleFilter.trim() === q.trim() ? '✓' : ''}</span>
                  <span className="bbs-item-body">
                    <span className="bbs-item-name">
                      <span className="bbs-badge bbs-badge-vin">Filter</span>
                      Show only “{q.trim()}” on the board
                    </span>
                    <span className="bbs-item-meta">
                      Matches year, make, model, trim or VIN. Every word must match.
                    </span>
                  </span>
                </button>
              )}
              {vins.map((v) => {
                const ymm = [v.model_year, v.make, v.model].filter(Boolean).join(' ');
                return (
                  <button
                    key={`v:${v.vin}`}
                    type="button"
                    className="bbs-item"
                    onClick={() => { onPickVin(v.vin, v.seller_uid); close(); }}
                  >
                    <span className="bbs-check" />
                    <span className="bbs-item-body">
                      <span className="bbs-item-name">
                        <span className="bbs-badge bbs-badge-vin">VIN</span>
                        {ymm || v.vin}
                      </span>
                      <span className="bbs-item-meta">{v.vin}{v.seller_name ? ` · ${v.seller_name}` : ''}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
